import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, Search, ShieldCheck, ShieldAlert } from "lucide-react";
import { api, errorToMessage } from "@/lib/apiClient";
import { WARR } from "@/constants/testIds";

const STATUS_COLORS = {
  ACTIVE: "bg-emerald-100 text-emerald-800",
  EXPIRED: "bg-slate-200 text-slate-600",
  VOIDED: "bg-rose-100 text-rose-800",
};
const TYPE_COLORS = {
  STANDARD: "bg-sky-100 text-sky-800",
  EXTENDED: "bg-indigo-100 text-indigo-800",
  RENEWAL: "bg-teal-100 text-teal-800",
};

function Chip({ text, cls }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls || "bg-slate-100 text-slate-700"}`}>
      {text}
    </span>
  );
}

export default function WarrantiesPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, total_pages: 1 });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [warrType, setWarrType] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const params = useMemo(() => ({
    page, page_size: pageSize,
    q: q || undefined,
    status: status || undefined,
    warranty_type: warrType || undefined,
  }), [page, pageSize, q, status, warrType]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/warranties", { params });
      setRows(data.items);
      setMeta(data.meta);
    } catch (e) {
      setError(errorToMessage(e, "Failed to load warranties"));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="warranties-page">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Warranties</h1>
          <p className="text-sm text-slate-500 mt-1">
            Standard, extended and renewal coverage. Auto-issued after each completed sale.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={load}
            className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 grid grid-cols-1 sm:grid-cols-4 gap-3 shadow-sm">
        <div className="sm:col-span-2 relative">
          <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
            data-testid={WARR.searchInput}
            placeholder="Customer · Mobile · Invoice · SO · Barcode · Serial"
            className="w-full rounded-md border border-slate-200 pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800" />
        </div>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          data-testid={WARR.statusFilter}
          className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
          <option value="">All statuses</option>
          {["ACTIVE","EXPIRED","VOIDED"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={warrType} onChange={(e) => { setWarrType(e.target.value); setPage(1); }}
          data-testid={WARR.typeFilter}
          className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
          <option value="">All types</option>
          {["STANDARD","EXTENDED","RENEWAL"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm" data-testid={WARR.listTable}>
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Passport · Serial</th>
              <th className="text-left px-4 py-3">Sales Order · Invoice</th>
              <th className="text-left px-4 py-3">Plan</th>
              <th className="text-left px-4 py-3">Period</th>
              <th className="text-right px-4 py-3">Days left</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Certificate</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-16 text-center text-slate-400">No warranties match.</td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} data-testid={WARR.listRow(r.id)}
                onClick={() => navigate(`/admin/warranties/${r.id}`)}
                className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                <td className="px-4 py-3"><Chip text={r.warranty_type} cls={TYPE_COLORS[r.warranty_type]} /></td>
                <td className="px-4 py-3">
                  <div className="text-slate-900 font-medium">{r.customer_name || "—"}</div>
                  <div className="text-xs text-slate-500">{r.mobile || ""}</div>
                </td>
                <td className="px-4 py-3 text-xs">
                  <div className="font-mono text-slate-800">{r.passport_barcode || "—"}</div>
                  <div className="font-mono text-slate-500">{r.passport_serial || r.serial_number || ""}</div>
                </td>
                <td className="px-4 py-3 text-xs">
                  <div className="font-mono">{r.so_no || "—"}</div>
                  <div className="font-mono text-slate-500">{r.invoice_no || ""}</div>
                </td>
                <td className="px-4 py-3 text-xs">
                  <div>{r.plan_label}</div>
                  <div className="text-slate-500">{r.plan_code}</div>
                </td>
                <td className="px-4 py-3 text-xs">
                  <div>{r.starts_on}</div>
                  <div className="text-slate-500">→ {r.ends_on}</div>
                </td>
                <td className="px-4 py-3 text-right text-xs font-mono">
                  {r.effective_status === "ACTIVE" ? (
                    <span className="text-emerald-700">{r.days_left}</span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    {r.effective_status === "ACTIVE"
                      ? <ShieldCheck size={12} className="text-emerald-600" />
                      : <ShieldAlert size={12} className="text-slate-400" />}
                    <Chip text={r.effective_status} cls={STATUS_COLORS[r.effective_status]} />
                  </div>
                </td>
                <td className="px-4 py-3 text-xs font-mono text-slate-700">
                  {r.certificate_no || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex items-center justify-between px-4 py-3 text-xs text-slate-500 border-t border-slate-100">
          <span>{meta.total} warrant{meta.total === 1 ? "y" : "ies"}</span>
          <div className="flex items-center gap-2">
            <button data-testid={WARR.pagePrev} disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 disabled:opacity-40">Prev</button>
            <span>Page {page} of {meta.total_pages || 1}</span>
            <button data-testid={WARR.pageNext} disabled={page >= (meta.total_pages || 1)}
              onClick={() => setPage(page + 1)}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 disabled:opacity-40">Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}
